import React, { useEffect } from "react";
import { X } from "lucide-react";
import clsx from "clsx";

interface ModalProps {
  /** Controls whether the modal is visible */
  open: boolean;
  /** Called when clicking outside or pressing ESC */
  onClose: () => void;
  /** Modal content */
  children: React.ReactNode;
  /** Optional modal title */
  title?: string;
  /** Optional className overrides */
  className?: string;
  /** Optional size */
  size?: "sm" | "md" | "lg";
}

/**
 * Generic, responsive modal.
 *
 * - Closes on ESC or backdrop click.
 * - Adapts to mobile (full-screen) or desktop (centered card).
 * - Prevents body scroll when open.
 * - Renders in-place (no portal) to avoid DOM cleanup issues.
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  className,
  size = "md",
}) => {
  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  // Handle ESC key press
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 backdrop-blur-sm px-3 sm:px-0"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      <div
        className={clsx(
          "relative bg-white rounded-lg shadow-lg w-full sm:w-auto max-h-[90vh] overflow-y-auto transition-transform",
          {
            "sm:max-w-sm": size === "sm",
            "sm:max-w-md": size === "md",
            "sm:max-w-lg": size === "lg",
          },
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b px-4 py-3">
          {title && (
            <h2 id="modal-title" className="text-lg font-semibold text-gray-800">
              {title}
            </h2>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

